import {useEffect, useRef, useSyncExternalStore} from 'react';
import {useSelector} from 'react-redux';
import {
  selectPendingShootingActions,
  selectPhase,
  selectWarPhaseStage,
} from '../../redux/reducers/sharedStateReducer/selectors';
import {selectInspectedHexKey, selectSelectedUnitId} from '../../redux/reducers/privateStateReducer/selectors';
import {PHASES, WAR_PHASE_STAGES} from '../../constants/phases';
import {checkIfShotResultHeld, subscribeToShotsInFlight} from '../effects/shotFlightTracker';

/**
 * Придержан ли итог этого выстрела для интерфейса: залп еще летит либо только
 * что упал (пауза после попадания — SHOT_ANIMATION.resultPanelDelayMs)
 */
export const useIsShotResultHeld = (actionId) => useSyncExternalStore(
  subscribeToShotsInFlight,
  () => Boolean(actionId) && checkIfShotResultHeld(actionId)
);

/**
 * Можно ли показывать в футере результат стрельбы выбранного отряда (панель
 * SelectedUnitShooting и ее заголовок).
 *
 * ПАНЕЛЬ ОТКРЫВАЕТСЯ ПОСЛЕ ПОПАДАНИЯ ЗАЛПА, А НЕ С ВЫСТРЕЛОМ. Экшен выстрела
 * меняет состояние сразу, и панель выезжала в тот же миг — поверх нижней части
 * поля, где нередко стоит отряд-цель: анимацию попадания по нему было не
 * видно. Теперь, пока залп летит, панель не открывается, а после попадания
 * ждет еще полсекунды: итог на поле (урон на цели, линия выстрела) сначала
 * виден целиком. Выстрел без анимации (скрытая вкладка, быстрая перемотка)
 * панель открывает сразу — ждать там нечего.
 *
 * УЖЕ ОТКРЫТАЯ ПАНЕЛЬ НЕ ЗАКРЫВАЕТСЯ. Если результат стрельбы и так показан
 * (переброс костей повторяет залп; игрок смотрит результат прежнего выстрела
 * и переключился на отряд, чей залп еще в воздухе), прятать панель на время
 * полета и паузы незачем — поле она уже закрывала, а мигание только мешало бы.
 * То же, когда футер был открыт осмотром ячейки: с осмотра стрелка футер сам
 * переключается на его выстрел (см. Footer), и вместо смены содержимого он
 * схлопывался бы на время полета и вырастал снова.
 *
 * Хук один на футер (см. Footer): его ответ раздается заголовку, тулбару и
 * футеру зрителя, чтобы все три судили одинаково.
 *
 * @returns {boolean}
 */
export const useShootingResultRevealed = () => {
  const phase = useSelector(selectPhase);
  const warPhaseStage = useSelector(selectWarPhaseStage);
  const selectedUnitId = useSelector(selectSelectedUnitId);
  const pendingShootingActions = useSelector(selectPendingShootingActions);
  const inspectedHexKey = useSelector(selectInspectedHexKey);

  const isShootingStage = phase === PHASES.WAR && warPhaseStage === WAR_PHASE_STAGES.SHOOTING;
  const shootingAction = isShootingStage && selectedUnitId
    ? (pendingShootingActions || []).find((action) => action.shooterUnitId === selectedUnitId)
    : null;
  const isShotResultHeld = useIsShotResultHeld(shootingAction?.id);

  // Была ли панель футера открыта на прошлой отрисовке: результатом стрельбы
  // или осмотром ячейки (он показывается поверх фазового содержимого)
  const wasPanelOpenRef = useRef(false);
  const isRevealed = Boolean(shootingAction) && (!isShotResultHeld || wasPanelOpenRef.current);
  const isPanelOpen = isRevealed || Boolean(inspectedHexKey);
  useEffect(() => {
    wasPanelOpenRef.current = isPanelOpen;
  });

  return isRevealed;
};
